import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine,
  faChartBar,
  faChartPie,
  faNewspaper,
  faEnvelope,
  faUsers,
  faEye,
  faThumbsUp,
  faShare,
  faCalendar,
  faFilter,
  faDownload,
  faRefresh,
  faArrowUp,
  faArrowDown,
  faMinus,
  faBuilding,
  faGlobe,
  faClock,
  faPercentage
} from '@fortawesome/free-solid-svg-icons';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { adminAPI } from '../../utils/api';
import AdminLayout from '../../components/admin/AdminLayout';

const Analytics = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState('30');
  
  // 数据状态
  const [overviewData, setOverviewData] = useState(null);
  const [newsAnalytics, setNewsAnalytics] = useState(null);
  const [contactAnalytics, setContactAnalytics] = useState(null);
  const [userAnalytics, setUserAnalytics] = useState(null);

  // 获取分析数据
  useEffect(() => {
    fetchAnalyticsData();
  }, [activeTab, dateRange]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = { period: dateRange };
      
      switch (activeTab) {
        case 'overview': {
          const overviewResponse = await adminAPI.getDashboard(params);
          if (overviewResponse.data.status === 'success') {
            setOverviewData(overviewResponse.data.data);
          }
          break;
        }
          
        case 'news': {
          const newsResponse = await adminAPI.getNewsAnalytics(params);
          if (newsResponse.data.status === 'success') {
            setNewsAnalytics(newsResponse.data.data);
          }
          break;
        }
          
        case 'contacts': {
          const contactResponse = await adminAPI.getContactAnalytics(params);
          if (contactResponse.data.status === 'success') {
            setContactAnalytics(contactResponse.data.data);
          }
          break;
        }
          
        case 'users': {
          const userResponse = await adminAPI.getUserAnalytics(params);
          if (userResponse.data.status === 'success') {
            setUserAnalytics(userResponse.data.data);
          }
          break;
        }
          
        default:
          break;
      }
    } catch (err) {
      setError(err.message || '获取分析数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 导出数据
  const handleExport = async () => {
    try {
      const response = await adminAPI.exportAnalytics({
        type: activeTab,
        period: dateRange
      });
      
      // 创建下载链接
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `analytics_${activeTab}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('导出失败');
    }
  };

  // 获取趋势图标
  const getTrendIcon = (trend) => {
    if (trend > 0) return { icon: faArrowUp, color: 'text-green-500' };
    if (trend < 0) return { icon: faArrowDown, color: 'text-red-500' };
    return { icon: faMinus, color: 'text-gray-500' };
  };

  // 图表颜色
  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#84CC16', '#F97316'];

  // 渲染概览页面
  const renderOverview = () => {
    if (!overviewData) return null;

    return (
      <div className="space-y-6">
        {/* 统计卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            {
              title: '新闻文章',
              value: overviewData.stats?.news?.total || 0,
              trend: overviewData.stats?.news?.trend || 0,
              icon: faNewspaper,
              color: 'bg-blue-500'
            },
            {
              title: '客户咨询',
              value: overviewData.stats?.contacts?.total || 0,
              trend: overviewData.stats?.contacts?.trend || 0,
              icon: faEnvelope,
              color: 'bg-green-500'
            },
            {
              title: '系统用户',
              value: overviewData.stats?.users?.total || 0,
              trend: overviewData.stats?.users?.trend || 0,
              icon: faUsers,
              color: 'bg-purple-500'
            },
            {
              title: '总浏览量',
              value: overviewData.stats?.views?.total || 0,
              trend: overviewData.stats?.views?.trend || 0,
              icon: faEye,
              color: 'bg-orange-500'
            }
          ].map((stat, index) => {
            const trendInfo = getTrendIcon(stat.trend);
            return (
              <div key={index} className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className={`flex-shrink-0 ${stat.color} rounded-md p-3`}>
                    <FontAwesomeIcon icon={stat.icon} className="h-6 w-6 text-white" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">{stat.title}</dt>
                      <dd className="flex items-baseline">
                        <div className="text-2xl font-semibold text-gray-900">
                          {stat.value.toLocaleString()}
                        </div>
                        <div className={`ml-2 flex items-baseline text-sm font-semibold ${trendInfo.color}`}>
                          <FontAwesomeIcon icon={trendInfo.icon} className="self-center flex-shrink-0 h-3 w-3" />
                          <span className="ml-1">
                            {Math.abs(stat.trend)}%
                          </span>
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* 趋势图表 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* 发布趋势 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">发布趋势</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={overviewData.trends?.publishing || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="news" stroke="#3B82F6" name="新闻" />
                <Line type="monotone" dataKey="contacts" stroke="#10B981" name="咨询" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* 访问趋势 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">访问趋势</h3>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={overviewData.trends?.traffic || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Area type="monotone" dataKey="views" stackId="1" stroke="#3B82F6" fill="#3B82F6" name="浏览量" />
                <Area type="monotone" dataKey="likes" stackId="1" stroke="#10B981" fill="#10B981" name="点赞" />
                <Area type="monotone" dataKey="shares" stackId="1" stroke="#F59E0B" fill="#F59E0B" name="分享" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* 最近活动 */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">最近活动</h3>
          <div className="flow-root">
            <ul className="-mb-8">
              {(overviewData.recentActivities || []).map((activity, index) => (
                <li key={index}>
                  <div className="relative pb-8">
                    {index !== overviewData.recentActivities.length - 1 && (
                      <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" />
                    )}
                    <div className="relative flex space-x-3">
                      <div>
                        <span className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white ${
                          activity.type === 'news' ? 'bg-blue-500' :
                          activity.type === 'contact' ? 'bg-green-500' :
                          activity.type === 'user' ? 'bg-purple-500' : 'bg-gray-500'
                        }`}>
                          <FontAwesomeIcon 
                            icon={
                              activity.type === 'news' ? faNewspaper :
                              activity.type === 'contact' ? faEnvelope :
                              activity.type === 'user' ? faUsers : faEye
                            } 
                            className="h-4 w-4 text-white" 
                          />
                        </span>
                      </div>
                      <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                        <div>
                          <p className="text-sm text-gray-500">
                            {activity.description}
                          </p>
                        </div>
                        <div className="text-right text-sm whitespace-nowrap text-gray-500">
                          <time dateTime={activity.timestamp}>
                            {new Date(activity.timestamp).toLocaleString()}
                          </time>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  };

  // 渲染新闻分析
  const renderNewsAnalytics = () => {
    if (!newsAnalytics) return null;

    return (
      <div className="space-y-6">
        {/* 新闻统计 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            {
              title: '总文章数',
              value: newsAnalytics.stats?.total || 0,
              icon: faNewspaper,
              color: 'bg-blue-500'
            },
            {
              title: '总浏览量',
              value: newsAnalytics.stats?.totalViews || 0,
              icon: faEye,
              color: 'bg-green-500'
            },
            {
              title: '总点赞数',
              value: newsAnalytics.stats?.totalLikes || 0,
              icon: faThumbsUp,
              color: 'bg-purple-500'
            },
            {
              title: '总分享数',
              value: newsAnalytics.stats?.totalShares || 0,
              icon: faShare,
              color: 'bg-orange-500'
            }
          ].map((stat, index) => (
            <div key={index} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className={`flex-shrink-0 ${stat.color} rounded-md p-3`}>
                  <FontAwesomeIcon icon={stat.icon} className="h-6 w-6 text-white" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">{stat.title}</dt>
                    <dd className="text-2xl font-semibold text-gray-900">
                      {stat.value.toLocaleString()}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 图表区域 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* 分类分布 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">分类分布</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={newsAnalytics.categoryStats || []}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {(newsAnalytics.categoryStats || []).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* 作者统计 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">作者统计</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={newsAnalytics.authorStats || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#3B82F6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* 热门文章 */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">热门文章</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    标题
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    浏览量
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    点赞数
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    分享数
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    发布时间
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(newsAnalytics.popularNews || []).map((news, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 truncate max-w-xs">
                        {news.title}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {news.views?.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {news.likes?.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {news.shares?.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(news.publishedAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // 渲染联系分析
  const renderContactAnalytics = () => {
    if (!contactAnalytics) return null;

    return (
      <div className="space-y-6">
        {/* 联系统计 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            {
              title: '总咨询数',
              value: contactAnalytics.stats?.total || 0,
              icon: faEnvelope,
              color: 'bg-blue-500'
            },
            {
              title: '待处理',
              value: contactAnalytics.stats?.pending || 0,
              icon: faClock,
              color: 'bg-yellow-500'
            },
            {
              title: '已完成',
              value: contactAnalytics.stats?.completed || 0,
              icon: faCheckCircle,
              color: 'bg-green-500'
            },
            {
              title: '转化率',
              value: `${contactAnalytics.stats?.conversionRate || 0}%`,
              icon: faPercentage,
              color: 'bg-purple-500'
            }
          ].map((stat, index) => (
            <div key={index} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className={`flex-shrink-0 ${stat.color} rounded-md p-3`}>
                  <FontAwesomeIcon icon={stat.icon} className="h-6 w-6 text-white" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">{stat.title}</dt>
                    <dd className="text-2xl font-semibold text-gray-900">
                      {typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 图表区域 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* 咨询类型分布 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">咨询类型分布</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={contactAnalytics.typeStats || []}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {(contactAnalytics.typeStats || []).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* 时间趋势 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">咨询趋势</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={contactAnalytics.timeStats || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="#3B82F6" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* 响应时间分析 */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">响应时间分析</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">
                {contactAnalytics.responseTime?.average || 0}h
              </div>
              <div className="text-sm text-gray-500">平均响应时间</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">
                {contactAnalytics.responseTime?.fastest || 0}h
              </div>
              <div className="text-sm text-gray-500">最快响应时间</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-red-600">
                {contactAnalytics.responseTime?.slowest || 0}h
              </div>
              <div className="text-sm text-gray-500">最慢响应时间</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 渲染用户分析
  const renderUserAnalytics = () => {
    if (!userAnalytics) return null;

    return (
      <div className="space-y-6">
        {/* 用户统计 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            {
              title: '总用户数',
              value: userAnalytics.stats?.total || 0,
              icon: faUsers,
              color: 'bg-blue-500'
            },
            {
              title: '活跃用户',
              value: userAnalytics.stats?.active || 0,
              icon: faCheckCircle,
              color: 'bg-green-500'
            },
            {
              title: '管理员',
              value: userAnalytics.stats?.admins || 0,
              icon: faShield,
              color: 'bg-red-500'
            },
            {
              title: '编辑',
              value: userAnalytics.stats?.editors || 0,
              icon: faUserCog,
              color: 'bg-purple-500'
            }
          ].map((stat, index) => (
            <div key={index} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className={`flex-shrink-0 ${stat.color} rounded-md p-3`}>
                  <FontAwesomeIcon icon={stat.icon} className="h-6 w-6 text-white" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">{stat.title}</dt>
                    <dd className="text-2xl font-semibold text-gray-900">
                      {stat.value.toLocaleString()}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 图表区域 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* 角色分布 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">角色分布</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={userAnalytics.roleStats || []}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {(userAnalytics.roleStats || []).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* 注册趋势 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">注册趋势</h3>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={userAnalytics.registrationStats || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Area type="monotone" dataKey="count" stroke="#3B82F6" fill="#3B82F6" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* 活跃度分析 */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">用户活跃度</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={userAnalytics.activityStats || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="logins" fill="#3B82F6" name="登录次数" />
              <Bar dataKey="actions" fill="#10B981" name="操作次数" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* 页面标题和操作 */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">数据分析</h1>
            <p className="mt-1 text-sm text-gray-500">
              查看系统各项数据的统计分析
            </p>
          </div>
          
          <div className="flex items-center space-x-3">
            {/* 时间范围选择 */}
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="7">最近7天</option>
              <option value="30">最近30天</option>
              <option value="90">最近90天</option>
              <option value="365">最近一年</option>
            </select>
            
            <button
              onClick={fetchAnalyticsData}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <FontAwesomeIcon icon={faRefresh} className="mr-2" />
              刷新
            </button>
            
            <button
              onClick={handleExport}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <FontAwesomeIcon icon={faDownload} className="mr-2" />
              导出
            </button>
          </div>
        </div>

        {/* 标签页导航 */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'overview', name: '概览', icon: faChartLine },
              { id: 'news', name: '新闻分析', icon: faNewspaper },
              { id: 'contacts', name: '联系分析', icon: faEnvelope },
              { id: 'users', name: '用户分析', icon: faUsers }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`group inline-flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <FontAwesomeIcon 
                  icon={tab.icon} 
                  className={`mr-2 h-4 w-4 ${
                    activeTab === tab.id ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'
                  }`} 
                />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* 内容区域 */}
        {error ? (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <FontAwesomeIcon icon={faExclamationTriangle} className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">加载失败</h3>
                <p className="mt-1 text-sm text-red-700">{error}</p>
                <button
                  onClick={fetchAnalyticsData}
                  className="mt-2 text-sm text-red-800 underline hover:text-red-900"
                >
                  重试
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div>
            {activeTab === 'overview' && renderOverview()}
            {activeTab === 'news' && renderNewsAnalytics()}
            {activeTab === 'contacts' && renderContactAnalytics()}
            {activeTab === 'users' && renderUserAnalytics()}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default Analytics;